import React, { useMemo } from 'react';
import { TreeItem } from '@mui/x-tree-view';
import { TextField } from '@mui/material';
import type { Folder } from '../services/folder.service';
import { buildNodeKey } from '../utils/nodeId';
import { ProjectTreeItem } from './ProjectTreeItem'; // Import ProjectTreeItem
import TreeItemLabel from './TreeItemLabel';
import { ITestCase } from '../types/interfaces/testCase.interface'; // Needed for TestCaseLoadingState type

// Replicate TestCaseLoadingState interface (or import if shared)
interface TestCaseLoadingState {
  loading: boolean;
  data: ITestCase[] | null;
  error?: string | null;
}

interface FolderTreeItemProps {
  folder: Folder;
  renamingNodeId: string;
  renameValue: string;
  onRenameChange: (value: string) => void;
  onRenameCancel: () => void;
  onRenameSubmit: () => void;
  cutProjectIds: number[];
  loadedTestCases: Record<number, TestCaseLoadingState>;
  onContextMenu: (
    event: React.MouseEvent,
    nodeId: string,
    nodeType: 'folder' | 'project' | 'testcase',
    parentId?: number
  ) => void;
  onSelectedItemsChange: (event: React.SyntheticEvent | null, itemIds: string[] | string) => void;
  filterText: string;
  ancestorMatched?: boolean;
  showModifiedDates: boolean;
}

// Use React.forwardRef if necessary for MUI components, but likely not needed here
export const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  folder,
  renamingNodeId,
  renameValue,
  onRenameChange,
  onRenameCancel,
  onRenameSubmit,
  cutProjectIds,
  loadedTestCases,
  onContextMenu,
  onSelectedItemsChange,
  filterText,
  ancestorMatched = false,
  showModifiedDates,
}) => {
  const itemId = buildNodeKey({ type: 'folder', id: folder.folderId });
  const isRenaming = renamingNodeId === itemId;
  const folderLabel = folder.name;
  const searchTerm = filterText.trim().toLowerCase();
  const isMatch = searchTerm && folder.name.toLowerCase().includes(searchTerm);
  const nextAncestorMatched = ancestorMatched || !!isMatch;

  // Calculate folder statistics
  const folderStats = useMemo(() => {
    if (!showModifiedDates) return null;

    const calculateStats = (folder: Folder): {
      projectCount: number;
      testCaseCount: number;
    } => {
      let stats = {
        projectCount: 0,
        testCaseCount: 0
      };

      // Add direct projects
      stats.projectCount += folder.projects.length;

      // Process direct projects
      folder.projects.forEach(project => {
        // Get test case count
        const testCases = loadedTestCases[project.testProjectID]?.data;
        if (testCases) {
          stats.testCaseCount += testCases.length;
        }
      });

      // Recursively process subfolders
      folder.folders?.forEach(subfolder => {
        const subStats = calculateStats(subfolder);
        stats.projectCount += subStats.projectCount;
        stats.testCaseCount += subStats.testCaseCount;
      });

      return stats;
    };

    return calculateStats(folder);
  }, [folder, loadedTestCases, showModifiedDates]);

  return (
    <TreeItem
      key={itemId}
      itemId={itemId}
      label={
        isRenaming ? (
          // Prevent click propagation when renaming
          <div onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
            <TextField
              value={renameValue}
              onChange={(e) => onRenameChange(e.target.value)}
              onBlur={(e) => {
                e.stopPropagation();
                onRenameCancel(); // Cancel on blur
              }}
              size="small"
              autoFocus
              variant="standard"
              inputProps={{
                onKeyDownCapture: (e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    onRenameSubmit();
                  } else if (e.key === 'Escape') {
                    onRenameCancel();
                  }
                },
              }}
            />
          </div>
        ) : (
          <TreeItemLabel 
            text={folderLabel} 
            searchTerm={filterText}
            projectCount={folderStats?.projectCount}
            testCaseCount={folderStats?.testCaseCount}
            id={folder.folderId}
          />
        )
      }
      onContextMenu={(e) => onContextMenu(e, itemId, 'folder', folder.folderId)}
      // Folder icons are handled by the parent SimpleTreeView slots
    >
      {/* Render Subfolders Recursively */}
      {folder.folders?.map((subfolder) => (
        <FolderTreeItem
          key={buildNodeKey({ type: 'folder', id: subfolder.folderId })}
          folder={subfolder}
          renamingNodeId={renamingNodeId}
          renameValue={renameValue}
          onRenameChange={onRenameChange}
          onRenameCancel={onRenameCancel}
          onRenameSubmit={onRenameSubmit}
          cutProjectIds={cutProjectIds}
          loadedTestCases={loadedTestCases}
          onContextMenu={onContextMenu}
          onSelectedItemsChange={onSelectedItemsChange}
          filterText={filterText}
          ancestorMatched={nextAncestorMatched}
          showModifiedDates={showModifiedDates}
        />
      ))}
      {/* Render Projects within this Folder */}
      {folder.projects.map((project) => (
        <ProjectTreeItem
          key={buildNodeKey({ type: 'project', id: project.testProjectID })}
          project={project}
          parentFolderId={folder.folderId} // Pass parent ID
          cutProjectIds={cutProjectIds}
          loadedTestCases={loadedTestCases}
          onContextMenu={onContextMenu as any} // Pass context menu handler (adjust type if needed)
          onSelectedItemsChange={onSelectedItemsChange}
          filterText={filterText}
          ancestorMatched={nextAncestorMatched}
          showModifiedDates={showModifiedDates}
        />
      ))}
    </TreeItem>
  );
}; 